/**
 * Landing hero.
 *
 * The first screen has to answer "what is this?" before a visitor reads any
 * feature list, so it is a value proposition, two calls to action and a product
 * mock (a dark city grid with four routes converging on the recommended area).
 *
 * The mock is drawn with plain SVG and static sample content. It never reads an
 * event, a participant or a recommendation, and every decorative element is
 * hidden from assistive technology so the section still reads as text.
 */

import { ArrowRight, MapPin, Route } from 'lucide-react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { PlainButton } from '../ui/button'
import { cn } from '../../lib/format'

export interface LandingHeroProps {
  /** Destination of the primary action, decided by the session. */
  primaryTo: string
  /** Label of the primary action. */
  primaryLabel: string
  /** Element id the secondary action scrolls to; the how-it-works section. */
  secondaryTarget: string
  /** Label of the secondary action. */
  secondaryLabel: string
  className?: string
}

/**
 * Departure areas shown around the recommended one in the mock.
 *
 * Three of the group's six members, which is what the summary line at the bottom
 * of the panel counts. The names are sample content shared with the larger preview
 * further down the page, so the two panels describe the same fictional outing.
 */
const MOCK_PARTICIPANTS = [
  { id: 'xujiahui', areaKey: 'home.previewCandidateTwo' },
  { id: 'wujiaochang', areaKey: 'home.previewCandidateThree' },
  { id: 'pudong', areaKey: 'home.problemItemOne' },
]

export function LandingHero({
  primaryTo,
  primaryLabel,
  secondaryTarget,
  secondaryLabel,
  className,
}: LandingHeroProps) {
  const { t } = useTranslation()

  return (
    <section className={cn('landing-hero', className)} aria-labelledby="landing-hero-title">
      <div className="landing-container grid gap-6 py-6 sm:gap-10 sm:py-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-12 lg:py-20">
        <div className="flex flex-col items-start gap-3 sm:gap-4">
          <span className="text-landing-accent inline-flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
            <Route className="size-4" aria-hidden />
            {t('home.eyebrow')}
          </span>

          <h1
            id="landing-hero-title"
            className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl"
          >
            {t('home.heroTitle')}
          </h1>

          <p className="text-landing-muted max-w-xl text-base sm:text-lg">{t('home.heroSubtitle')}</p>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Link className="landing-cta-primary" to={primaryTo}>
              {primaryLabel}
              <ArrowRight className="size-4" aria-hidden />
            </Link>
            {/* A real button rather than an in-page anchor: with a client router the
                anchor updates the hash without scrolling the document, which leaves
                the visitor looking at a hero that never moved. */}
            <PlainButton
              variant="ghost"
              className="landing-cta-secondary"
              aria-label={secondaryLabel}
              onClick={() =>
                document.getElementById(secondaryTarget)?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              {secondaryLabel}
            </PlainButton>
          </div>

          <p className="text-landing-muted text-xs">{t('home.heroMeta')}</p>
        </div>

        {/* Product mock: purely decorative, so the whole panel is hidden. */}
        <div
          className="bg-landing-panel relative overflow-hidden rounded-2xl border border-white/10 p-3 sm:p-6"
          aria-hidden
        >
          <div className="landing-hero-grid absolute inset-0 opacity-60" />

          <div className="text-landing-muted relative flex items-center gap-2 text-xs">
            <MapPin className="text-landing-accent size-3.5" />
            <span>{t('home.previewEventTitle')}</span>
            <span aria-hidden>·</span>
            <span>{t('home.previewParticipants')}</span>
          </div>

          <div className="relative mt-3 h-24 sm:h-40">
            <svg
              className="h-full w-full"
              viewBox="0 0 400 180"
              preserveAspectRatio="none"
              fill="none"
            >
              <path
                d="M0 24 C 110 24, 150 90, 200 90"
                className="stroke-landing-accent"
                strokeWidth="2"
                strokeDasharray="6 6"
              />
              <path
                d="M0 150 C 120 150, 150 96, 200 90"
                className="stroke-landing-warm"
                strokeWidth="2"
              />
              <path
                d="M400 30 C 290 30, 250 90, 200 90"
                className="stroke-landing-accent"
                strokeWidth="2"
                strokeDasharray="6 6"
              />
              <path
                d="M400 156 C 280 156, 250 96, 200 90"
                className="stroke-landing-warm"
                strokeWidth="2"
              />
            </svg>

            <div className="border-landing-accent/60 bg-landing-surface absolute top-1/2 left-1/2 flex w-40 -translate-x-1/2 -translate-y-1/2 flex-col gap-0.5 rounded-xl border p-3 text-center">
              <span className="text-landing-ink text-sm font-semibold">
                {t('home.previewCandidateOne')}
              </span>
              <span className="text-landing-accent text-xs">{t('home.previewSubtitle')}</span>
            </div>
          </div>

          {/*
            Three departures side by side on every screen: stacked they added a
            hundred pixels of height to the mock for no extra meaning. A grid item
            keeps `min-width: auto`, so each cell carries `min-w-0` and truncates
            its own text instead of widening the column.
          */}
          <ul className="relative mt-3 grid grid-cols-3 gap-2">
            {MOCK_PARTICIPANTS.map((participant) => (
              <li
                key={participant.id}
                className="bg-landing-surface flex min-w-0 flex-col gap-0.5 rounded-xl border border-white/10 px-2 py-2 sm:px-3"
              >
                <span className="text-landing-ink truncate text-xs font-medium">
                  {t(participant.areaKey)}
                </span>
                <span className="text-landing-muted truncate text-[11px]">
                  {t('home.heroMockCommute')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
